import React, { useState, useEffect } from 'react'
import fetchHook from '../../../hooks/fetchMSSQL-hook'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import NoResults from '../../../components/NoResults/NoResults'
import PrepaidMulitplyChange from './PrepaidMulitplyChange'
import SearchFilter from '../../../components/Filter/SearchFilter'
import { NotificationManager } from 'react-notifications'

export default function PrepaidsUser() {
  const { fetchMssqlApi } = fetchHook()

  const [prepaids, setPrepaids] = useState([])
  const [sortField, setSortField] = useState('OwnerData')
  const [sortOrder, setSortOrder] = useState('asc')
  const [selectedPrepaids, setSelectedPrepaids] = useState([])
  const [prepaidMulitplyChangeState, setPrepaidMulitplyChangeState] = useState({})
  const [prepaidGroups, setPrepaidGroups] = useState([])
  const [statuses, setStatuses] = useState([])
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  const [isImporting, setIsImporting] = useState(false)

  const defaultFilter = {
    name: 'prepaidsUser',
    page: 1,
    rowsPerPage: 50,
    sortBy: "none",
    prepaidId: null,
    ownerData: '',
    printedCodeName: "",
    codeName: "",
    groupName: "",
    prepaidStatus: "",
    prepaidType: "",
    prepaidStatusId: null,
    prepaidTypeId: null,
    customerAbbr: null,
  }

  const [filter, setFilter] = useState(defaultFilter)
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 50,
  })

  const rowsPerPage = settingsFilter.rowsPerPage || filter.rowsPerPage || 50

  const search = (f) => {
    const currentFilter = f || filter

    const body = {
      PrepaidId: currentFilter.prepaidId ?? null,
      OwnerData: currentFilter.ownerData || null,

      PrintedCode: currentFilter.printedCodeName || null,
      Code: currentFilter.codeName || null,

      PrepaidGroupId: currentFilter.prepaidGroupId || null,
      PrepaidStatusId: currentFilter.prepaidStatusId || null,
      PrepaidTypeId: currentFilter.prepaidTypeId || null,

      CustomerAbbr: currentFilter.customerAbbr || null,
    }

    setSortField(currentFilter.sortField || 'OwnerData')
    setSortOrder(currentFilter.sortOrder || 'asc')

    fetchMssqlApi(
      'prepaids-filter',
      { method: 'POST', data: body, hideNotification: true },
      res => setPrepaids(res || [])
    )
  }

  useEffect(() => {
    fetchMssqlApi('prepaid-groups', {}, res => setPrepaidGroups(res ?? []))
    fetchMssqlApi('prepaid-status', {}, res => setStatuses(res ?? []))
  }, [])

  useEffect(() => {
    search(defaultFilter)
  }, [])

  const handleCheckboxChange = (prepaidId) => {
    setSelectedPrepaids(prev =>
      prev.includes(prepaidId)
        ? prev.filter(id => id !== prepaidId)
        : [...prev, prepaidId]
    )
  }

  const selectAllPrepaids = () =>
    setSelectedPrepaids(prepaids.map(p => p.PrepaidId))

  const unselectAllPrepaids = () =>
    setSelectedPrepaids([])

  const handleSubmitMulitplyChange = () => {
    const payload = {
      PrepaidIds: selectedPrepaids,
      Values: prepaidMulitplyChangeState,
    }

    fetchMssqlApi(
      'prepaids-mulitply-changes',
      { method: 'POST', data: payload },
      () => {
        search(filter)
        setSelectedPrepaids([])
        setPrepaidMulitplyChangeState({})
      },
      error => {
        NotificationManager.error(error.message)
      }
    )
  }

  const getImportErrorMessage = (error) => {
    const data = error?.response?.data || error?.data || error || {}
    const errors = data?.errors || error?.errors

    if (Array.isArray(errors) && errors.length) {
      const visibleErrors = errors.slice(0, 6)

      return (
        <div>
          {visibleErrors.map((err, index) => {
            const row = err.RowNumber ? `Wiersz ${err.RowNumber}` : "Plik"
            const field = err.FieldName ? `, ${err.FieldName}` : ""
            const message = err.Message || err.message || "Błąd walidacji"
            const value = err.Value ? ` (${err.Value})` : ""

            return (
              <div key={index}>
                <strong>{row}</strong>
                {field}: {message}{value}
              </div>
            )
          })}

          {errors.length > visibleErrors.length && (
            <div>
              I jeszcze {errors.length - visibleErrors.length} błędów
            </div>
          )}
        </div>
      )
    }

    return data?.message || error?.message || "Nie udało się zaimportować pliku"
  }

  const finishImport = (input) => {
    if (input) {
      input.value = ""
    }

    setIsImporting(false)
  }

  const importPrepaids = async (e) => {
    const input = e.target
    const file = input.files?.[0]

    if (input) {
      input.value = ""
    }

    if (!file || isImporting) return

    const ext = file.name.split(".").pop()?.toLowerCase()

    if (ext !== "xlsx") {
      NotificationManager.error("Wybierz plik .xlsx", "Import")
      return
    }

    const data = new FormData()
    data.append("file", file)

    setIsImporting(true)

    let errorHandled = false
    let imported = false

    try {
      await fetchMssqlApi(
        "prepaids/import",
        {
          method: "POST",
          data,
          hideNotification: true
        },
        res => {
          imported = true
          NotificationManager.success(
            res?.message || "Import zakończony poprawnie",
            "Import"
          )
        },
        error => {
          errorHandled = true
          NotificationManager.error(
            getImportErrorMessage(error),
            "Import",
            10000
          )
        }
      )
    } catch (error) {
      if (!errorHandled) {
        NotificationManager.error(
          getImportErrorMessage(error),
          "Import",
          10000
        )
      }
    } finally {
      finishImport(input)
    }

    if (imported) {
      search(filter)
    }
  }

  const makeSortComparator = (sortBy) => {
    if (!sortBy || sortBy === "none") return () => 0

    const [field, order = "asc", type = "text"] = sortBy.split("|").map(s => s.trim())

    const norm = (v) => {
      if (v === null || v === undefined) return null

      if (type === "price") return Number(String(v).replace(",", "."))
      if (type === "bool") return v ? 1 : 0
      if (type === "date") {
        const t = new Date(v).getTime()
        return Number.isFinite(t) ? t : String(v).toUpperCase()
      }

      return String(v).toUpperCase()
    }

    return (a, b) => {
      const A = norm(a?.[field])
      const B = norm(b?.[field])

      if (A === B) return 0
      if (A === null) return 1
      if (B === null) return -1

      const res = A < B ? -1 : 1
      return order === "asc" ? res : -res
    }
  }

  const prepaidSortFields = [
    { label: "Właściciel", field: "OwnerData", type: "text" },
    { label: "Klient", field: "CustomerName", type: "text" },
    { label: "Grupa", field: "GroupName", type: "text" },
    { label: "Status", field: "PrepaidStatus", type: "text" },
    { label: "Kredyt", field: "CreditValue", type: "price" },
    { label: "Typ", field: "PrepaidType", type: "text" },
    { label: "Numer karty", field: "PrintedCode", type: "text" },
  ]

  const sortedPrepaids = React.useMemo(() => {
    const cmp = makeSortComparator(filter.sortBy)
    return [...prepaids].sort(cmp)
  }, [prepaids, filter.sortBy])

  return (
    <>
      <SearchFilter
        filterName="prepaidsUser"
        defaultFilter={defaultFilter}
        onFilterChange={setFilter}
        onSettingsChange={s => setSettingsFilter(prev => ({ ...prev, ...s }))}
        search={search}
        reportList={[{ reportId: 108, reportColor: "primary" }]}
        totalCount={sortedPrepaids.length}
        dataList={sortedPrepaids}
        isSorted
        sortFields={prepaidSortFields}
        forNow
      />
      <div className='card'>
        <h5 className="card-header">
          <div className='d-flex justify-content-between mt-1'>
            Karty prepaid - {prepaids.length}
            <Link to={`/prepaids/card/0`} className="btn text-nowrap btn-outline-secondary">
              <i className="fas fa-plus" />
            </Link>
          </div>
        </h5>

        <div className='overflow-auto' style={{ maxHeight: "70%" }}>
          {selectedPrepaids.length ? (
            <PrepaidMulitplyChange
              PrepaidMulitplyChange={prepaidMulitplyChangeState}
              setPrepaidMulitplyChange={setPrepaidMulitplyChangeState}
              prepaidGroups={prepaidGroups}
              statuses={statuses}
              handleSubmit={handleSubmitMulitplyChange}
              selectAll={selectAllPrepaids}
              unselectAll={unselectAllPrepaids}
            />
          ) : null}

          {prepaids.length ? (
            <div
              className="overflow-auto custom-scroll"
              style={{
                maxHeight: `${windowHeight - 266}px`,
                overflowY: 'auto',
              }}
            >
              <table className='table table-striped'>
                <thead>
                  <tr className='headers-color pe-none text-center'>
                    <th></th>
                    <th className='align-middle'>
                      Wlaściciel karty prepaid
                      {sessionStorage.getItem('clientId') === "console" && (<><br /> Klient</>)}
                    </th>
                    <th className='align-middle'>
                      Dodatkowe informacje <br /> Grupa prepaid
                    </th>
                    <th className='align-middle'>
                      Częstotliwość zasilania / Status <br />
                      Bieżący kredyt / Typ prepaid <br />
                      Numer karty prepaid
                    </th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {sortedPrepaids.slice(
                    (filter.page - 1) * rowsPerPage,
                    filter.page * rowsPerPage
                  ).map((prepaid, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className='d-flex w-100 h-100 justify-content-center align-items-center'>
                          <input
                            type="checkbox"
                            style={{ width: "22px", height: "22px" }}
                            checked={selectedPrepaids.includes(prepaid.PrepaidId)}
                            onChange={() => handleCheckboxChange(prepaid.PrepaidId)}
                          />
                        </div>
                      </td>

                      <td className='text-center'>
                        <h6>{prepaid.OwnerData}</h6>
                        {sessionStorage.getItem('clientId') === "console" && (
                          <h6>{prepaid.CustomerName}</h6>
                        )}
                      </td>

                      <td className='text-center'>
                        <div>{prepaid.AdditionalData}</div>
                        <div>
                          <Link
                            to={`group/${prepaid.PrepaidGroupId}`}
                            className="mt-1 btn btn-outline-secondary btn-sm"
                          >
                            {prepaid.GroupName}
                          </Link>
                        </div>
                      </td>

                      <td className='text-center'>
                        <div className='d-flex nowrap justify-content-center'>
                          <h6>
                            <span className="badge badge-primary align-middle mr-2">
                              {prepaid.PrepaidTimespanName}
                            </span>
                          </h6>
                          <h6>
                            <span className={`badge badge-${+(prepaid.PrepaidStatusId) <= 2 ? 'success' : 'danger'} align-middle`}>
                              {prepaid.PrepaidStatus}
                            </span>
                          </h6>
                        </div>

                        <div className='d-flex nowrap justify-content-center'>
                          <h6>
                            <span className="badge mr-2 badge-success">
                              {Intl.NumberFormat('pl-PL', { style: "currency", currency: "PLN" })
                                .format((prepaid.CreditValue).toFixed(2))}
                            </span>
                          </h6>
                          <h6>
                            <span className="badge bg-yellow text-white align-middle">
                              {prepaid.PrepaidType}
                            </span>
                          </h6>
                        </div>

                        <div>
                          <h6>{`${prepaid.PrintedCode} (${prepaid.Code})`}</h6>
                        </div>
                      </td>

                      <td>
                        <div className='d-flex justify-content-end border-0'>
                          <Link
                            className="mr-4 btn btn-outline-primary btn-sm"
                            to={`card/${prepaid.PrepaidId}`}
                          >
                            Edytuj
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <NoResults />}
        </div>
      </div>
    </>
  )
}